import { apiClient } from '../utils/api';

export type BillingStatus = 'pending' | 'active' | 'past_due' | 'canceled' | 'exempt' | 'incomplete';
export type BillingInterval = 'month' | 'year';
export type BillableFeature = 'invoices' | 'matching' | 'vorlagen' | 'pflegedoku' | 'aiemail';

export const BILLABLE_FEATURES: BillableFeature[] = [
  'invoices',
  'matching',
  'vorlagen',
  'pflegedoku',
  'aiemail',
];

/**
 * Add-ons in their evaluation phase: active, but not charged yet (mirrors
 * EVALUATION_FEATURES in the backend billing.service).
 */
export const EVALUATION_FEATURES: BillableFeature[] = ['aiemail'];

export const FEATURE_LABELS: Record<BillableFeature, string> = {
  invoices: 'Rechnungen',
  matching: 'Matching',
  vorlagen: 'Vorlagen',
  pflegedoku: 'Pflegedokumentation',
  aiemail: 'KI-E-Mail',
};

export interface AgencyBilling {
  id: string;
  agencyId: string;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  status: BillingStatus;
  billingInterval: BillingInterval;
  billingQuantity: number;
  pendingQuantity: number;
  livePatientCount?: number;
  freeFeature: string | null;
  enabledFeatures: string[];
  /** Testphase-Add-ons, die dieser Agentur über den Lizenzpreis verkauft wurden. */
  includedFeatures?: string[];
  firstBillingDate: string | null;
  nextBillingDate: string | null;
  isExempt: boolean;
  exemptReason: string | null;
  createdAt: string;
  updatedAt: string;
  agency?: {
    id: string;
    name: string;
    email: string;
    isActive: boolean;
    /** Land der Agentur (AT/DE/CH) */
    country?: string | null;
  };
}

export interface SetupBillingDto {
  interval: BillingInterval;
  firstBillingDate: string;
  freeFeature?: string | null;
  enabledFeatures: string[];
}

export interface UpdateBillingDto {
  isExempt?: boolean;
  exemptReason?: string;
  enabledFeatures?: string[];
  status?: string;
}

export interface StripeInvoice {
  id: string;
  amount_paid: number;
  amount_due: number;
  currency: string;
  status: string;
  created: number;
  period_start: number;
  period_end: number;
  /** Real service period (from the line items) — the invoice-level period
   *  collapses to a single instant for advance-billed subscriptions. */
  service_period_start?: number;
  service_period_end?: number;
  /** Net / VAT / gross in cents. */
  net_amount?: number;
  tax_amount?: number;
  gross_amount?: number;
  hosted_invoice_url: string | null;
  invoice_pdf: string | null;
  description: string | null;
  /** Rechnungsnummer (aus dem Snapshot). */
  number?: string | null;
  /** Eigener Zahlungsstatus: draft | finalized | payment_pending | paid | payment_failed | void | uncollectible */
  payment_status?: string;
  /** Eingefrorene Positionen (nur bei Snapshot-Rechnungen). */
  lines?: InvoiceSnapshotLine[];
  snapshot?: boolean;
}

export interface InvoiceSnapshotLine {
  kind: 'recurring' | 'catchup' | 'first_period' | 'other' | string;
  description: string;
  quantity: number;
  unitAmount: number | null;
  amount: number;
  periodStart: number | null;
  periodEnd: number | null;
  taxRate: number | null;
  priceId: string | null;
  productName: string | null;
  stripeLineId: string | null;
}

export interface InvoiceBreakdown {
  recurringQuantity: number | null;
  recurringPeriod: { start: number; end: number } | null;
  catchupQuantity: number;
  catchupPeriod: { start: number; end: number } | null;
  catchupClients: Array<{
    clientId: string;
    clientName: string | null;
    activatedAt: string | null;
    deactivatedAt: string | null;
    status: string;
    amountCents: number | null;
  }>;
  additionsDuringCycle: Array<{ clientId: string; clientName: string | null; at: string | null }>;
  removalsDuringCycle: Array<{ clientId: string; clientName: string | null; at: string | null }>;
  events: Array<{ id: string; eventType: string; message: string | null; createdAt: string }>;
}

export interface InvoiceDetail {
  invoice: StripeInvoice;
  breakdown: InvoiceBreakdown;
}

/**
 * Darstellung der Stripe-Rechnungsstatus. Wird von beiden Rechnungslisten im
 * Super-Admin-Bereich genutzt (Abonnements und Monatsbericht), damit dort
 * nicht zwei verschiedene Beschriftungen entstehen.
 */
export const INVOICE_STATUS_CONFIG: Record<string, { color: string; label: string }> = {
  paid: { color: 'success', label: 'Bezahlt' },
  open: { color: 'warning', label: 'Offen' },
  draft: { color: 'default', label: 'Entwurf' },
  void: { color: 'default', label: 'Storniert' },
  uncollectible: { color: 'error', label: 'Uneinbringlich' },
};

/** Ergebnis des Adress-Abgleichs mit Stripe, pro Agentur. */
export interface AddressSyncResult {
  agencyId: string;
  agencyName: string | null;
  status: 'synced' | 'skipped' | 'failed';
  message: string;
}

export interface AddressSyncSummary {
  total: number;
  synced: number;
  skipped: number;
  failed: number;
  results: AddressSyncResult[];
}

/** Bezahltes korrigiert man bei Stripe nur per Gutschrift, Storniertes gar nicht. */
export const isInvoiceCancelable = (status: string): boolean =>
  status !== 'paid' && status !== 'void';

/** What the agency still has to do to complete its subscription (banner). */
export interface PaymentAction {
  reason: 'pay_invoice' | 'add_payment_method' | 'enable_auto_payment' | null;
  payUrl: string | null;
  amountDue: number | null;
  currency: string | null;
}

/** Siehe backend billing/payment-overview.ts */
export type PaymentOverviewState = 'auto' | 'manual' | 'no_method' | 'no_subscription' | 'manual_fixed';
export interface PaymentOverview {
  agencyId: string;
  state: PaymentOverviewState;
  methodType: string | null;
  brand: string | null;
  last4: string | null;
  expMonth: number | null;
  expYear: number | null;
  subscriptionStatus: string | null;
  nextChargeAt: string | null;
  /** Cent, brutto */
  nextAmount: number | null;
  /** Cent, netto (ohne USt) */
  nextAmountNet?: number | null;
  currency: string | null;
  interval: 'day' | 'week' | 'month' | 'year' | null;
  intervalCount: number | null;
  /** Nur manuelle Zahlungen */
  anchorDay?: number | null;
  amountNet?: number | null;
  vatRate?: number | null;
  note?: string | null;
}

export interface ManualBillingInput {
  /** Cent netto */
  amountNet: number;
  vatRate: number;
  interval: 'month' | 'quarter' | 'year';
  nextDate: string;
  anchorDay?: number | null;
  note?: string | null;
}

export class BillingService {
  static async getPaymentAction(refresh = false): Promise<PaymentAction> {
    const response = await apiClient.get<{ status: string; data: PaymentAction }>(
      '/billing/my/payment-action',
      { params: refresh ? { refresh: 1 } : undefined },
    );
    return response.data.data;
  }

  static async getBillingInfo(): Promise<AgencyBilling | null> {
    const response = await apiClient.get<{ status: string; data: AgencyBilling | null }>('/billing/my');
    return response.data.data;
  }

  static async getMyInvoices(): Promise<StripeInvoice[]> {
    const response = await apiClient.get<{ status: string; data: StripeInvoice[] }>('/billing/my/invoices');
    return response.data.data;
  }

  /** Zahlungsmittel + automatische Abbuchung + nächste Rechnung je Agentur (Super-Admin). */
  static async getPaymentOverview(refresh = false): Promise<PaymentOverview[]> {
    const response = await apiClient.get<{ status: string; data: PaymentOverview[] }>(
      '/billing/admin/payment-overview',
      { params: refresh ? { refresh: 1 } : undefined },
    );
    return response.data.data;
  }

  static async setManualBilling(agencyId: string, manualBilling: ManualBillingInput | null): Promise<void> {
    await apiClient.put(`/billing/admin/${agencyId}/manual-billing`, { manualBilling });
  }

  /** Agentur von der Abrechnung befreien bzw. die Befreiung aufheben (Super-Admin). */
  static async setExempt(agencyId: string, isExempt: boolean, reason?: string | null): Promise<void> {
    await apiClient.put(`/billing/admin/${agencyId}/exempt`, { isExempt, reason: reason || null });
  }

  /** Testphase-Add-on als "im Lizenzpreis enthalten" markieren (Super-Admin, nur Anzeige). */
  static async setFeatureIncluded(agencyId: string, feature: BillableFeature, included: boolean): Promise<void> {
    await apiClient.put(`/billing/admin/${agencyId}/included-feature`, { feature, included });
  }

  static async getAllBilling(): Promise<AgencyBilling[]> {
    const response = await apiClient.get<{ status: string; data: AgencyBilling[] }>('/billing/admin/all');
    return response.data.data;
  }

  static async setupAgencyBilling(agencyId: string, data: SetupBillingDto): Promise<AgencyBilling> {
    const response = await apiClient.post<{ status: string; data: AgencyBilling }>(
      `/billing/admin/${agencyId}/setup`,
      data,
    );
    return response.data.data;
  }

  static async updateAgencyBilling(agencyId: string, data: UpdateBillingDto): Promise<AgencyBilling> {
    const response = await apiClient.put<{ status: string; data: AgencyBilling }>(
      `/billing/admin/${agencyId}`,
      data,
    );
    return response.data.data;
  }

  static async createPortalSession(flow?: 'payment_method_update'): Promise<string> {
    const response = await apiClient.post<{ status: string; data: { url: string } }>(
      '/billing/my/portal',
      flow ? { flow } : {},
    );
    return response.data.data.url;
  }

  static async getAgencyInvoices(agencyId: string): Promise<StripeInvoice[]> {
    const response = await apiClient.get<{ status: string; data: StripeInvoice[] }>(
      `/billing/admin/${agencyId}/invoices`,
    );
    return response.data.data;
  }

  /** Snapshot + Herleitung einer Rechnung (Super-Admin, Support). */
  static async getAgencyInvoiceDetail(agencyId: string, invoiceId: string): Promise<InvoiceDetail> {
    const response = await apiClient.get<{ status: string; data: InvoiceDetail }>(
      `/billing/admin/${agencyId}/invoices/${encodeURIComponent(invoiceId)}`,
    );
    return response.data.data;
  }

  /**
   * Storniert eine Rechnung bei Stripe (Super-Admin). Entwürfe werden gelöscht,
   * finalisierte Rechnungen auf "void" gesetzt — beides endgültig.
   */
  static async cancelAgencyInvoice(
    agencyId: string,
    invoiceId: string,
  ): Promise<{ id: string; status: string; action: 'voided' | 'deleted' }> {
    const response = await apiClient.post<{
      status: string;
      message: string;
      data: { id: string; status: string; action: 'voided' | 'deleted' };
    }>(`/billing/admin/${agencyId}/invoices/${encodeURIComponent(invoiceId)}/cancel`);
    return response.data.data;
  }

  /**
   * Überträgt die aktuelle Agentur-Adresse (inkl. Stiege/Stock/Tür) an alle
   * bestehenden Stripe-Kunden. Bereits ausgestellte Rechnungen ändert Stripe
   * dadurch nicht — die Adresse wird beim Ausstellen eingefroren.
   */
  static async syncStripeAddresses(): Promise<AddressSyncSummary> {
    const response = await apiClient.post<{
      status: string;
      message: string;
      data: AddressSyncSummary;
    }>('/billing/admin/sync-addresses');
    return response.data.data;
  }
}
